import React from "react";
import { useNavigate } from "react-router-dom";

const GreenStaking = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: "Sustainable Consensus",
      description: "Our proof-of-authority consensus uses 99.9% less energy than traditional proof-of-work systems.",
      icon: "🌱"
    },
    {
      title: "Instant Finality",
      description: "Transactions are confirmed instantly with no risk of reorganization.",
      icon: "⚡"
    },
    {
      title: "Carbon Neutral",
      description: "All network operations are carbon offset through verified environmental projects.",
      icon: "♻️"
    },
    {
      title: "Auto-compounding",
      description: "Rewards are automatically compounded to maximize your returns without extra gas fees.",
      icon: "📈"
    }
  ];

  const stakingTiers = [
    { name: "Basic", apy: "5.2%", minStake: "100 XEC", features: ["Basic rewards", "Weekly payouts"] },
    { name: "Premium", apy: "7.8%", minStake: "1,000 XEC", features: ["Enhanced rewards", "Daily payouts", "Governance rights"] },
    { name: "Eco-Validator", apy: "12.5%", minStake: "10,000 XEC", features: ["Max rewards", "Hourly payouts", "Governance voting", "Validator status"] }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark pt-20">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-eco-green hover:text-eco-blue mb-8 transition-colors"
        >
          ← Back to Ecosystem
        </button>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-gradient-to-br from-eco-green to-eco-blue rounded-3xl flex items-center justify-center text-eco-black text-3xl mx-auto mb-6">
            🌿
          </div>
          <h1 className="text-5xl font-black gradient-text mb-4">Green Staking</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Earn sustainable rewards while supporting eco-friendly blockchain infrastructure. 
            Our green staking mechanism combines high yields with environmental responsibility.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div key={index} className="glass-card p-6 rounded-3xl border border-eco-green border-opacity-20">
              <div className="text-3xl mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-gray-300">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Staking Tiers */}
        <div className="glass-card rounded-3xl p-8 mb-16 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Staking Tiers</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stakingTiers.map((tier, index) => (
              <div key={index} className="bg-eco-dark rounded-2xl p-6 border border-gray-700 hover:border-eco-green transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                <div className="text-3xl font-black text-eco-green mb-4">{tier.apy} APY</div>
                <p className="text-gray-400 mb-4">Min Stake: {tier.minStake}</p>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-eco-blue">
                      <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="w-full py-3 bg-eco-green text-eco-black font-bold rounded-xl hover:bg-eco-blue transition-colors">
                  Start Staking
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Environmental Impact */}
        <div className="glass-card rounded-3xl p-8 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Environmental Impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Carbon Savings</h3>
              <p className="text-gray-300 mb-6">
                Compared to traditional proof-of-work blockchains, our green staking solution saves approximately 
                <span className="text-eco-green font-bold"> 2.3 million tons of CO2 </span>
                annually - equivalent to taking 500,000 cars off the road.
              </p>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Energy Consumption</span>
                  <span className="text-eco-green font-bold">-99.9%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Carbon Footprint</span>
                  <span className="text-eco-green font-bold">Carbon Neutral</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Transaction Speed</span>
                  <span className="text-eco-green font-bold">+400%</span>
                </div>
              </div>
            </div>
            <div className="bg-eco-dark rounded-2xl p-6">
              <h4 className="text-lg font-bold text-white mb-4">Real-time Impact</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">CO2 Saved Today</span>
                    <span className="text-eco-green">6,324 kg</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-eco-green h-2 rounded-full" style={{width: '75%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Energy Saved</span>
                    <span className="text-eco-green">8.7 MWh</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-eco-blue h-2 rounded-full" style={{width: '82%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Active Stakers</span>
                    <span className="text-eco-purple">12,458</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-eco-purple h-2 rounded-full" style={{width: '65%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GreenStaking;